import { CONSENT_COOKIE_NAME } from '~/lib/constants/cookies'
import { buildUploadOutcomeParams, buildUploadSelectionParams } from '~/lib/utils/upload-telemetry'

export class AnalyticsService {
  constructor({ clientCookieService }) {
    this.clientCookieService = clientCookieService
    const { gtag } = useGtag()
    this.gtag = gtag
  }

  /**
   * @param {momentshare.models.order.Order} order
   * @returns {void}
   */
  purchaseEvent(order) {
    // Free demos and 100%-discount orders land here too. The server skips them on the
    // same value (reportPurchaseToMeta): a €0 Purchase teaches Meta to find freebie takers.
    if (!order.totalInclVat) return

    const totalValue = order.orderLines.reduce((acc, line) => acc + line.cost, 0)
    const totalVat = order.orderLines.reduce((acc, line) => acc + line.vat, 0)
    const coupon =
      order.description.indexOf('DISCOUNT CODE') > -1 ? this.getDiscountCodeFromDescription(order.description) : null
    const items = this.mapOrderLinesToEventItems(order.orderLines)
    const gclid = this.getGclid()

    this.gtag('event', 'purchase', {
      // Pairs this browser event with the server-side CAPI Purchase from the Mollie
      // webhook, so Meta counts one purchase instead of two. GTM exposes gtag
      // parameters under the eventModel prefix, so the Meta tag's Event ID must read
      // the data layer variable `eventModel.event_id` — matching how the existing
      // value/currency/transaction_id variables are defined.
      event_id: `order_${order.id}`,
      ecommerce: {
        transaction_id: order.id,
        value: parseFloat(totalValue.toFixed(2)),
        vat: parseFloat(totalVat.toFixed(2)),
        currency: 'EUR',
        ...(gclid && { gclid }),
        ...(coupon && { coupon }),
        items,
      },
    })

    // Gclid needs to be reported only once
    this.removeGclid()
  }

  /**
   * Someone is looking at the pricing page. Deliberately carries no value: three tiers
   * are on screen and none is chosen yet, so any single figure would be invented.
   *
   * GTM maps this to Meta's ViewContent, the way it maps `purchase` to Purchase.
   *
   * @param {{contentName: string, contentIds: string[]}} input
   * @returns {void}
   */
  viewContentEvent({ contentName, contentIds }) {
    this.gtag('event', 'view_item', {
      ecommerce: {
        currency: 'EUR',
        content_name: contentName,
        content_ids: contentIds,
      },
    })
  }

  /**
   * Someone has opened the order form with a package resolved — the step Meta calls
   * InitiateCheckout. The server reports AddPaymentInfo separately, at submit, so this
   * has no server counterpart and needs no event_id.
   *
   * @param {{packageId: number, packageName: string, value: number}} input Value excluding VAT
   * @returns {void}
   */
  initiateCheckoutEvent({ packageId, packageName, value }) {
    // A package that never loaded, or a €0 demo, is not a checkout worth reporting.
    if (!value) return

    const price = parseFloat(value.toFixed(2))

    this.gtag('event', 'begin_checkout', {
      ecommerce: {
        value: price,
        currency: 'EUR',
        content_name: packageName,
        content_ids: [String(packageId)],
        items: [{ item_id: packageId, item_name: packageName, price, quantity: 1 }],
      },
    })
  }

  /**
   * Aggregate trend: a file selection completed (the @input event fired).
   * @param {{files: (File[]|FileList), isIOS: boolean, isSafari: boolean, pickToChangeMs?: (number|null)}} input
   */
  trackUploadSelection({ files, isIOS, isSafari, pickToChangeMs }) {
    try {
      this.gtag('event', 'upload_selection', buildUploadSelectionParams({ files, isIOS, isSafari, pickToChangeMs }))
    } catch {
      // Telemetry must never break the upload flow.
    }
  }

  /**
   * Aggregate trend: an upload run finished.
   * @param {{summary: object, isIOS: boolean}} input
   */
  trackUploadOutcome({ summary, isIOS }) {
    try {
      this.gtag('event', 'upload_outcome', buildUploadOutcomeParams({ summary, isIOS }))
    } catch {
      // Telemetry must never break the upload flow.
    }
  }

  getConsentCookieData() {
    try {
      return JSON.parse(this.clientCookieService.get(CONSENT_COOKIE_NAME))
    } catch {
      return {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: 'granted',
      }
    }
  }

  updateConsentCookie(cookieData) {
    this.clientCookieService.set(CONSENT_COOKIE_NAME, JSON.stringify(cookieData), { expires: 180 })

    this.gtag('consent', 'update', cookieData)
  }

  getDiscountCodeFromDescription(description) {
    const from = description.indexOf('CODE') + 5
    const end = description.indexOf(')')

    return description.substring(from, end)
  }

  saveGclid(gclid) {
    this.clientCookieService.set('ms_gclid', gclid, { expires: 28 })
  }

  saveFbclid(fbclid) {
    this.clientCookieService.set('ms_fbclid', fbclid, { expires: 28 })
  }

  saveFromExternalSource(externalSource) {
    this.clientCookieService.set('ms_fromExternalSource', externalSource, { expires: 28 })
  }

  saveReferrer() {
    const referrer = document?.referrer
    if (referrer) {
      this.clientCookieService.set('ms_fromReferrer', referrer, { expires: 28 })
    }
  }

  getGclid() {
    return this.clientCookieService.get('ms_gclid')
  }

  getFbclid() {
    return this.clientCookieService.get('ms_fbclid')
  }

  getFromExternalSource() {
    return this.clientCookieService.get('ms_fromExternalSource')
  }

  removeGclid() {
    return this.clientCookieService.delete('ms_gclid')
  }

  removeFbclid() {
    return this.clientCookieService.delete('ms_fbclid')
  }

  mapOrderLinesToEventItems(orderLines) {
    return orderLines.map((orderLine) => {
      return {
        item_id: orderLine.id,
        item_name: orderLine.description,
        price: parseFloat(orderLine.cost.toFixed(2)),
        quantity: 1,
      }
    })
  }

  getReferrer() {
    const referrers = []
    if (this.getGclid()) {
      referrers.push('Google Ads')
    }

    if (this.getFbclid()) {
      referrers.push('Meta Ads')
    }

    if (this.getFromExternalSource()) {
      referrers.push(this.getFromExternalSource())
    }

    referrers.push(this.clientCookieService.get('ms_fromReferrer') ?? 'Unknown')

    return referrers.join(',')
  }

  /**
   * Meta's click id. The pixel writes _fbc itself on landing, so prefer that;
   * rebuilding from ms_fbclid only covers visitors whose pixel never loaded.
   * @returns {string|null}
   */
  getFbc() {
    const fbc = this.clientCookieService.get('_fbc')
    if (fbc) return fbc

    const fbclid = this.getFbclid()
    if (!fbclid) return null

    // Format is fb.<subdomainIndex>.<creationTime>.<fbclid>. We never stored when
    // the click happened, so the fallback stamps "now" — good enough to match on.
    return `fb.1.${Date.now()}.${fbclid}`
  }

  /**
   * Match keys stored with the order, read back when the Mollie webhook fires the
   * server-side purchase events (Meta CAPI and the ChatGPT Ads Conversions API). IP and
   * user-agent are deliberately absent: the backend takes those from the request, since
   * client-sent values are spoofable.
   * @returns {{fbp: (string|null), fbc: (string|null), oppref: (string|null), obref: (string|null), gclid: (string|null), adConsent: boolean}}
   */
  getAdMatchKeys() {
    const consent = this.getConsentCookieData()

    return {
      fbp: this.clientCookieService.get('_fbp') ?? null,
      fbc: this.getFbc(),
      // Written by the OpenAI pixel: oppref from the ?oppref= click reference ChatGPT
      // appends to ad landing URLs, obref as its browser id.
      oppref: this.clientCookieService.get('__oppref') ?? null,
      obref: this.clientCookieService.get('__obref') ?? null,
      // Stored in order_attribution; the Google Ads conversion is uploaded from the
      // Mollie webhook.
      gclid: this.getGclid() ?? null,
      // ad_user_data governs sending user data to an ad platform, which is what
      // CAPI does. ad_storage only covers storing the cookies themselves.
      adConsent: consent?.ad_user_data === 'granted',
    }
  }
}
